import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SendHorizontal, X } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { StatusBadge } from "@/components/shared/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useGetApiAgents } from "@/lib/api/generated/agents/agents";
import { useGetApiChannelConnections, useGetApiChannelDeployments } from "@/lib/api/generated/channels/channels";
import {
  getApiOutboundBroadcastsBroadcastId,
  getGetApiOutboundBroadcastsQueryKey,
  useGetApiOutboundBroadcasts,
  usePostApiOutboundBroadcasts,
} from "@/lib/api/generated/outbound-broadcasts/outbound-broadcasts";
import { useTenantDirectoryUsersQuery } from "@/lib/query/tenant-app-users";
import type {
  AgentRecordDto,
  ChannelConnectionResponse,
  ChannelDeploymentResponse,
  OutboundBroadcastResponse,
  User,
} from "@/lib/api/generated/control-api.schemas";
import { formatTimestamp, toastMutationError } from "@/lib/utils/formatting";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";

type DeployedChannel = {
  connection: ChannelConnectionResponse;
  deployment: ChannelDeploymentResponse;
};

type AudienceMode = "all" | "selected";
const BroadcastCompletionPollMs = 1000;

export function OutboundBroadcastsPage() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();
  const [agentId, setAgentId] = useState("");
  const [channelConnectionId, setChannelConnectionId] = useState("");
  const [audienceMode, setAudienceMode] = useState<AudienceMode>("all");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [message, setMessage] = useState("");
  const [isAwaitingCompletion, setIsAwaitingCompletion] = useState(false);
  useDocumentTitle("Outbound");

  const queryOptions = { query: { enabled: Boolean(tenantId) } };
  const agentsQuery = useGetApiAgents<AgentRecordDto[]>(queryOptions);
  const connectionsQuery = useGetApiChannelConnections<ChannelConnectionResponse[]>(queryOptions);
  const deploymentsQuery = useGetApiChannelDeployments<ChannelDeploymentResponse[]>(queryOptions);
  const directoryUsersQuery = useTenantDirectoryUsersQuery(tenantId ?? "");
  const broadcastsQuery = useGetApiOutboundBroadcasts({
    query: { enabled: Boolean(tenantId), refetchInterval: 10_000 },
  });

  const activeAgents = useMemo(
    () => (agentsQuery.data ?? []).filter((agent) => agent.agentId && agent.status === "active"),
    [agentsQuery.data],
  );
  const selectedAgentId = activeAgents.some((agent) => agent.agentId === agentId)
    ? agentId
    : activeAgents[0]?.agentId ?? "";

  const deployedChannels = useMemo(() => {
    const activeConnections = new Map(
      (connectionsQuery.data ?? [])
        .filter((connection) => connection.status === "active" && connection.linkStatus === "linked")
        .map((connection) => [connection.id, connection]),
    );
    return (deploymentsQuery.data ?? [])
      .filter((deployment) => deployment.status === "active" && deployment.allowDms)
      .map((deployment) => {
        const connection = activeConnections.get(deployment.channelConnectionId);
        return connection ? { connection, deployment } : null;
      })
      .filter((entry): entry is DeployedChannel => Boolean(entry));
  }, [connectionsQuery.data, deploymentsQuery.data]);

  const channelOptions = useMemo(
    () =>
      deployedChannels.filter(
        (entry) => !selectedAgentId || entry.deployment.agentId === selectedAgentId,
      ),
    [selectedAgentId, deployedChannels],
  );
  const selectedChannelConnectionId = channelOptions.some((entry) => entry.connection.id === channelConnectionId)
    ? channelConnectionId
    : channelOptions[0]?.connection.id ?? "";

  const eligibleUsers = useMemo(
    () =>
      (directoryUsersQuery.data?.items ?? [])
        .filter(
          (user) =>
            user.isIdentityActive &&
            user.isApplicationAssignmentActive &&
            user.phoneConfirmed &&
            Boolean(user.phone),
        )
        .toSorted((a, b) => userLabel(a).localeCompare(userLabel(b))),
    [directoryUsersQuery.data?.items],
  );

  const createBroadcast = usePostApiOutboundBroadcasts();

  const selectedUsers = useMemo(() => {
    const selected = new Set(selectedUserIds);
    return eligibleUsers.filter((user) => selected.has(user.id));
  }, [eligibleUsers, selectedUserIds]);
  const effectiveSelectedUserIds = selectedUsers.map((user) => user.id);

  const selectedChannel = channelOptions.find(
    (entry) => entry.connection.id === selectedChannelConnectionId,
  );
  const trimmedMessage = message.trim();
  const hasRecipients = audienceMode === "all" || effectiveSelectedUserIds.length > 0;
  const isSending = createBroadcast.isPending || isAwaitingCompletion;
  const canSubmit = Boolean(selectedAgentId && selectedChannel && trimmedMessage && hasRecipients) && !isSending;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedChannel) {
      return;
    }

    setIsAwaitingCompletion(true);
    try {
      const created = await createBroadcast.mutateAsync({
        data: {
          agentId: selectedAgentId,
          channelConnectionId: selectedChannel.connection.id,
          message: trimmedMessage,
          userIds: audienceMode === "selected" ? effectiveSelectedUserIds : null,
          clientRequestId: crypto.randomUUID(),
        },
      });
      const completed = await waitForBroadcastCompletion(created.broadcast.id);
      const sent = completed.broadcast.sentRecipients;
      const failed = completed.broadcast.failedRecipients;
      const skipped = completed.broadcast.skippedRecipients;
      toast.success(
        `Broadcast completed: ${sent} sent, ${failed} failed, ${skipped} skipped.`,
      );
      setMessage("");
      await queryClient.invalidateQueries({
        queryKey: getGetApiOutboundBroadcastsQueryKey(),
      });
    } catch (error) {
      toast.error(toastMutationError(error, "Failed to complete outbound broadcast"));
    } finally {
      setIsAwaitingCompletion(false);
    }
  };

  const isLoading =
    agentsQuery.isLoading ||
    connectionsQuery.isLoading ||
    deploymentsQuery.isLoading ||
    directoryUsersQuery.isLoading ||
    broadcastsQuery.isLoading;
  const broadcasts = broadcastsQuery.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
            Outbound
          </h2>
          <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
            Send one assistant message to eligible registered users through a deployed channel.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-4">
        <Card>
          <CardHeader>
            <CardTitle>New Broadcast</CardTitle>
            <CardDescription>
              Recipients are resolved by the API from active directory users with confirmed phone numbers.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="outbound-agent">Agent</Label>
                <Select
                  value={selectedAgentId || undefined}
                  onValueChange={(value) => {
                    setAgentId(value);
                    setChannelConnectionId("");
                  }}
                  disabled={activeAgents.length === 0}
                >
                  <SelectTrigger id="outbound-agent" className="w-full">
                    <SelectValue placeholder="Select agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeAgents.map((agent) => (
                      <SelectItem key={agent.agentId} value={agent.agentId!}>
                        {agent.name || agent.agentId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="outbound-channel">Channel</Label>
                <Select
                  value={selectedChannelConnectionId || undefined}
                  onValueChange={setChannelConnectionId}
                  disabled={channelOptions.length === 0}
                >
                  <SelectTrigger id="outbound-channel" className="w-full">
                    <SelectValue placeholder="Select deployed channel" />
                  </SelectTrigger>
                  <SelectContent>
                    {channelOptions.map(({ connection }) => (
                      <SelectItem key={connection.id} value={connection.id}>
                        {connection.displayName} ({connection.channelType})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Audience</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={audienceMode === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAudienceMode("all")}
                >
                  All
                </Button>
                <Button
                  type="button"
                  variant={audienceMode === "selected" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAudienceMode("selected")}
                >
                  Selected
                </Button>
              </div>
              {audienceMode === "selected" ? (
                <RecipientPicker
                  users={eligibleUsers}
                  selectedUserIds={selectedUserIds}
                  search={userSearch}
                  onSearchChange={setUserSearch}
                  onAdd={(userId) => {
                    setSelectedUserIds((current) =>
                      current.includes(userId) ? current : [...current, userId],
                    );
                    setUserSearch("");
                  }}
                  onRemove={(userId) => {
                    setSelectedUserIds((current) => current.filter((id) => id !== userId));
                  }}
                />
              ) : (
                <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                  {eligibleUsers.length} eligible user{eligibleUsers.length === 1 ? "" : "s"} with confirmed phone numbers.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="outbound-message">Message</Label>
              <Textarea
                id="outbound-message"
                minLength={1}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Write the exact message users should receive."
                className="min-h-32 resize-y"
              />
            </div>

            {activeAgents.length === 0 && (
              <p className="text-xs text-destructive">Create and activate an agent before sending outbound messages.</p>
            )}
            {activeAgents.length > 0 && channelOptions.length === 0 && (
              <p className="text-xs text-destructive">
                Select an agent with an active linked channel deployment that allows direct messages.
              </p>
            )}
            {audienceMode === "selected" && selectedUserIds.length === 0 && (
              <p className="text-xs text-destructive">Select at least one recipient.</p>
            )}

            <div className="flex items-center justify-between gap-3">
              <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                The API creates or reuses each user session before delivery. Recipients:{" "}
                {audienceMode === "selected" ? selectedUsers.length : eligibleUsers.length}.
              </p>
              <Button onClick={handleSubmit} disabled={!canSubmit}>
                <SendHorizontal className="h-4 w-4 mr-2" />
                {isSending ? "Sending..." : "Send Broadcast"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Selected Channel</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedChannel ? (
              <div className="grid gap-3 text-xs">
                <Metric label="Connection" value={selectedChannel.connection.displayName} />
                <Metric label="Type" value={selectedChannel.connection.channelType} />
                <Metric label="Deployment" value={selectedChannel.deployment.id} mono />
                <Metric label="Direct Messages" value={selectedChannel.deployment.allowDms ? "Allowed" : "Blocked"} />
              </div>
            ) : (
              <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                No active linked channel deployment is available for the selected agent.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Broadcast History</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              Loading broadcasts...
            </p>
          ) : broadcasts.length === 0 ? (
            <EmptyState
              icon={<SendHorizontal size={40} strokeWidth={1.2} />}
              title="No outbound broadcasts"
              description="Sent broadcasts and per-recipient delivery counts will appear here."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs" style={{ color: "var(--muted-foreground)" }}>
                    <th className="py-2 pr-3 font-medium">Created</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 pr-3 font-medium">Message</th>
                    <th className="py-2 pr-3 font-medium">Sent</th>
                    <th className="py-2 pr-3 font-medium">Failed</th>
                    <th className="py-2 pr-3 font-medium">Skipped</th>
                  </tr>
                </thead>
                <tbody>
                  {broadcasts.map((broadcast) => (
                    <BroadcastRow key={broadcast.id} broadcast={broadcast} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function BroadcastRow({ broadcast }: { broadcast: OutboundBroadcastResponse }) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-3 pr-3 whitespace-nowrap text-xs" style={{ color: "var(--muted-foreground)" }}>
        {formatTimestamp(broadcast.createdAtUtc)}
      </td>
      <td className="py-3 pr-3">
        <StatusBadge status={broadcast.status} />
      </td>
      <td className="py-3 pr-3 min-w-72">
        <p className="line-clamp-2" style={{ color: "var(--text-strong)" }}>
          {broadcast.messagePreview}
        </p>
        <p className="text-xs font-mono" style={{ color: "var(--muted-foreground)" }}>
          {broadcast.channelType}:{broadcast.channelConnectionId}
        </p>
      </td>
      <td className="py-3 pr-3 tabular-nums">{broadcast.sentRecipients}</td>
      <td className="py-3 pr-3 tabular-nums">{broadcast.failedRecipients}</td>
      <td className="py-3 pr-3 tabular-nums">{broadcast.skippedRecipients}</td>
    </tr>
  );
}

function RecipientPicker({
  users,
  selectedUserIds,
  search,
  onSearchChange,
  onAdd,
  onRemove,
}: {
  users: User[];
  selectedUserIds: string[];
  search: string;
  onSearchChange: (value: string) => void;
  onAdd: (userId: string) => void;
  onRemove: (userId: string) => void;
}) {
  const selected = new Set(selectedUserIds);
  const selectedUsers = users.filter((user) => selected.has(user.id));
  const normalizedSearch = search.trim().toLowerCase();
  const suggestions = users
    .filter((user) => !selected.has(user.id))
    .filter((user) => {
      if (!normalizedSearch) {
        return true;
      }
      return [
        user.displayName,
        user.email,
        user.phone ?? "",
        user.id,
      ].some((value) => value.toLowerCase().includes(normalizedSearch));
    })
    .slice(0, 8);

  return (
    <div className="space-y-2">
      <div className="min-h-10 rounded-md border border-input px-2 py-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          {selectedUsers.map((user) => (
            <Badge key={user.id} variant="outline" className="gap-1 pr-1">
              <span>{userLabel(user)}</span>
              <button
                type="button"
                className="rounded-full p-0.5 hover:bg-muted"
                onClick={() => onRemove(user.id)}
                aria-label={`Remove ${userLabel(user)}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={selectedUsers.length === 0 ? "Search users..." : "Add user..."}
            className="h-7 min-w-48 flex-1 border-0 px-1 shadow-none focus-visible:ring-0"
          />
        </div>
      </div>

      <div className="max-h-52 overflow-y-auto rounded-md border border-border">
        {suggestions.length > 0 ? (
          suggestions.map((user) => (
            <button
              key={user.id}
              type="button"
              className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left text-sm last:border-0 hover:bg-muted/40"
              onClick={() => onAdd(user.id)}
            >
              <span className="min-w-0">
                <span className="block truncate" style={{ color: "var(--text-strong)" }}>
                  {user.displayName || user.email || user.id}
                </span>
                <span className="block truncate text-xs" style={{ color: "var(--muted-foreground)" }}>
                  {[user.email, user.phone].filter(Boolean).join(" · ")}
                </span>
              </span>
              <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                Add
              </span>
            </button>
          ))
        ) : (
          <p className="px-3 py-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
            No eligible users match.
          </p>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <span style={{ color: "var(--muted-foreground)" }}>{label}</span>
      <p className={mono ? "font-mono break-all" : "break-words"} style={{ color: "var(--text-strong)" }}>
        {value}
      </p>
    </div>
  );
}

async function waitForBroadcastCompletion(broadcastId: string) {
  for (;;) {
    const detail = await getApiOutboundBroadcastsBroadcastId(broadcastId);
    if (detail.broadcast.status === "completed") {
      return detail;
    }
    await sleep(BroadcastCompletionPollMs);
  }
}

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function userLabel(user: User): string {
  return user.displayName || user.email || user.id;
}
